import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useTenant } from '@/contexts/tenant-context';
import { useDefaultWorkspace } from '@/hooks/use-default-workspace';
import { getCurrentTenantSlug, isCentralDomain, getCentralDomainUrl, getWorkspaceUrlFromObject } from '@/utils/tenant-utils';

// Central domain pages
import LandingPage from '@/pages/LandingPage';
import Auth from '@/pages/Auth';
import ResetPassword from '@/pages/ResetPassword';
import { VerifyCode } from '@/pages/VerifyCode';
import { InviteAccept } from '@/pages/InviteAccept';
import WorkspaceSelector from '@/pages/WorkspaceSelector';

// Workspace pages  
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import Chat from '@/pages/Chat';
import Plan from '@/pages/Plan';
import NotFound from '@/pages/NotFound';

/**
 * SubdomainRouter handles routing based on domain/subdomain
 * 
 * Routing Logic:
 * - {APP_DOMAIN} (central): Auth, workspace selection, landing
 * - workspace.{APP_DOMAIN}: Workspace-specific app
 * - custom.domain.com: Custom domain routing (if verified)
 * - localhost:8080: Development mode with tenant parameter
 */
export const SubdomainRouter: React.FC<{ children?: React.ReactNode }> = () => {
  const { user, loading: authLoading } = useAuth();
  const { tenant, loading: tenantLoading, error } = useTenant();
  const { shouldRedirectToDefault, redirectToDefaultWorkspace, defaultWorkspace } = useDefaultWorkspace();
  const [domainType, setDomainType] = useState<'central' | 'workspace' | 'unknown'>('unknown');
  const [workspaceSlug, setWorkspaceSlug] = useState<string | null>(null);

  useEffect(() => {
    const checkDomain = () => {
      const tenantSlug = getCurrentTenantSlug();
      const isCentral = isCentralDomain();
      
      
      
      if (isCentral) {
        // Central domain routing ({APP_DOMAIN})
        
        setDomainType('central');
      } else if (tenantSlug) {
        // Workspace subdomain routing (workspace.{APP_DOMAIN} OR localhost:8080/?tenant=slug)
        
        setDomainType('workspace');
        setWorkspaceSlug(tenantSlug);
      } else {
        // Unknown domain - could be custom domain or error
        
        setDomainType('unknown');
        setWorkspaceSlug(null);
      }
    };

    checkDomain();
    
    // Listen for URL changes
    window.addEventListener('popstate', checkDomain);
    return () => window.removeEventListener('popstate', checkDomain);
  }, []);

  // Handle default workspace redirection
  useEffect(() => {
    if (shouldRedirectToDefault) {
      redirectToDefaultWorkspace();
    }
  }, [shouldRedirectToDefault, redirectToDefaultWorkspace]);

  // If we're in a workspace domain but the tenant wasn't found, auto-redirect back to central
  useEffect(() => {
    if (domainType === 'workspace' && !tenant) {
      const timer = setTimeout(() => {
        window.location.href = getCentralDomainUrl();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [domainType, tenant]);

  // Show loading while determining domain type and auth status
  if (domainType === 'unknown' || authLoading || tenantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading WattFactory...</p>
        </div>
      </div>
    );
  }

  // Central domain routing (wattfactory.tech)
  if (domainType === 'central') {
    // If central domain and a workspace route is hit directly (e.g., /plan or /chat),
    // forward to the user's default workspace.
    if (user && (window.location.pathname === '/plan' || window.location.pathname === '/chat')) {
      if (defaultWorkspace) {
        const target = getWorkspaceUrlFromObject(defaultWorkspace, `${window.location.pathname}${window.location.search}`);
        window.location.href = target;
        return null;
      }
    }
    return (
      <BrowserRouter>
        <Routes>
          {/* Prevent brief 404 on central when hitting workspace routes directly */}
          <Route path="/plan" element={<LandingPage />} />
          <Route path="/chat" element={<LandingPage />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-code" element={<VerifyCode />} />
          <Route 
            path="/workspaces" 
            element={
              <ProtectedRoute>
                <WorkspaceSelector />
              </ProtectedRoute>
            } 
          />
          <Route path="/invite/:token" element={<InviteAccept />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Workspace subdomain routing (workspace.wattfactory.tech OR localhost:8080/?tenant=slug)
  if (domainType === 'workspace') {
    // Check if workspace exists and user has access
    if (!tenant) {
      // Auto-redirect handled by top-level effect above; show message meanwhile
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-2">Workspace Not Found</h1>
            <p className="text-muted-foreground mb-4">
              The workspace "{workspaceSlug}" does not exist or you don't have access.
            </p>
            {tenantLoading && (
              <p className="text-sm text-gray-500 mb-2">Loading workspace data...</p>
            )}
            {error && (
              <p className="text-sm text-red-500 mb-2">Error: {error}</p>
            )}
            <p className="text-sm text-gray-600 mb-4">
              Debug: User authenticated: {user ? 'Yes' : 'No'}, Tenant loading: {tenantLoading ? 'Yes' : 'No'}
            </p>
            <p className="text-sm text-blue-600 mb-4">
              Check console logs for detailed debugging info. Redirecting in 5 seconds...
            </p>
            <a 
              href={getCentralDomainUrl()} 
              className="text-blue-500 hover:text-blue-700 underline"
            >
              Return to WattFactory (or wait 5 seconds)
            </a>
          </div>
        </div>
      );
    }

    // Redirect to central auth if not authenticated
    if (!user) {
      const returnUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
      // Ensure staging environments and their subdomains always send auth
      // to the staging central domain
      window.location.href = getCentralDomainUrl(`/auth?returnUrl=${encodeURIComponent(returnUrl)}`);
      return null;
    }

    // Check if user should be redirected to default workspace first
    if (shouldRedirectToDefault) {
      // Show loading while redirect is happening
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Redirecting to your workspace...</p>
          </div>
        </div>
      );
    }

    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Navigate to={`/plan${window.location.search}`} />} />
          <Route 
            path="/plan" 
            element={
              <ProtectedRoute>
                <Plan />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/chat" 
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            } 
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    );
  }

  // Fallback - should not reach here
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-red-600 mb-2">Domain Error</h1>
        <p className="text-muted-foreground">Unable to determine application mode.</p>
      </div>
    </div>
  );
}; 